import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import {
  IMPERSONATION_BANNER_HEIGHT,
  IMPERSONATION_SESSION_QUERY_KEY,
  useImpersonationSessions,
} from '@hooks/impersonation';
import { INFINITE_NOTIFICATIONS_QUERY_KEY, NOTIFICATIONS_QUERY_KEY } from '@hooks/notifications';
import { ME_QUERY_KEY } from '@hooks/users';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import handleRequestError from '@utils/handleRequestError';

const ROUTE_HOME = '/';

export function ImpersonationBanner() {
  const history = useHistory();
  const { session, isImpersonating, exitImpersonation, isPending } = useImpersonationSessions({
    mutationKey: [[IMPERSONATION_SESSION_QUERY_KEY], [ME_QUERY_KEY]],
    resetKey: [[NOTIFICATIONS_QUERY_KEY], [INFINITE_NOTIFICATIONS_QUERY_KEY]],
    onExit: () => history.push(ROUTE_HOME),
    onError: handleRequestError,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Mounted only while a session is active (see the layout), so popover state
  // resets on unmount — no stale anchor survives an exit → re-impersonate cycle.
  if (!isImpersonating || !session) return null;

  const open = Boolean(anchorEl);
  const { name, email } = session.targetUser;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: IMPERSONATION_BANNER_HEIGHT,
        zIndex: (theme) => theme.zIndex.drawer + 2,
        bgcolor: 'error.dark',
        color: 'error.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ButtonBase
        aria-expanded={open}
        aria-haspopup="true"
        sx={{
          height: '100%',
          px: 1.5,
          gap: 0.75,
          borderRadius: 0,
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.12)' },
        }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <SupervisorAccountIcon sx={{ fontSize: 16 }} />
        <Typography
          component="span"
          sx={{ fontWeight: 'bold', letterSpacing: 0.5, lineHeight: 1, textTransform: 'uppercase' }}
          variant="caption"
        >
          <FormattedMessage defaultMessage="Impersonation" id="IMPERSONATION.BANNER.LABEL" />
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={open}
        slotProps={{ paper: { sx: { mt: 0.5, p: 2, minWidth: 240, maxWidth: 320 } } }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setAnchorEl(null)}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography color="text.secondary" variant="caption">
              <FormattedMessage
                defaultMessage="You are impersonating"
                id="IMPERSONATION.BANNER.SUBTITLE"
              />
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }} variant="body2">
              {name}
            </Typography>
            <Typography color="text.secondary" sx={{ wordBreak: 'break-all' }} variant="body2">
              {email}
            </Typography>
          </Box>
          <Button
            fullWidth
            color="error"
            disabled={isPending}
            size="small"
            startIcon={
              isPending ? (
                <CircularProgress color="inherit" size={14} />
              ) : (
                <LogoutIcon fontSize="small" />
              )
            }
            sx={{ textTransform: 'none' }}
            variant="contained"
            onClick={() => {
              void exitImpersonation(session.id);
            }}
          >
            <FormattedMessage defaultMessage="Exit impersonation" id="IMPERSONATION.BANNER.EXIT" />
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
}
